import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  BarChart3,
  ChevronRight,
  Package,
  Pencil,
  Plus,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { adminApi, ApiError, apiErrorMessage } from "@/lib/api";
import { adminConfig } from "@/lib/config";
import { can, requiredRole } from "@/lib/permissions";
import {
  queryKeys,
  useCurrentIdentityQuery,
  useProductQuery,
  useProductsQuery,
} from "@/lib/queries";
import type { EntitlementType, Product } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  EmptyState,
  InlineAlert,
  LoadingRows,
} from "@/components/admin/feedback";
import { PermissionHint } from "@/components/admin/status";
import { SelectableRow } from "@/components/admin/selectable-row";
import { Select } from "@/components/ui/select";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { ProductStatusDialog } from "@/components/admin/product-status-dialog";
import { ProductFeatureCatalogDialog } from "@/components/admin/product-feature-catalog-dialog";

export function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const identityQuery = useCurrentIdentityQuery();
  const productsQuery = useProductsQuery();
  const queryClient = useQueryClient();
  const products = productsQuery.data?.products ?? [];
  const [filter, setFilter] = useState("");
  // Status filter is local UI state; the default matches the daily workflow of
  // managing active products. "all" keeps the full catalog available.
  const [statusFilter, setStatusFilter] = useState("active");
  const selectedID = searchParams.get("product") ?? "";
  const visibleProducts = products
    .filter((product) => statusFilter === "all" || product.status === statusFilter)
    .filter((product) =>
      `${product.id} ${product.key} ${product.name}`
        .toLowerCase()
        .includes(filter.trim().toLowerCase()),
    );
  // Keep the detail pane on the URL-selected Product even when the status
  // filter hides it (e.g. Disable while viewing); fall back only when the ID
  // is unknown to the catalog.
  const selectedInCatalog = Boolean(selectedID) && products.some(
    (product) => product.id === selectedID,
  );
  const selectedIDWithFallback = selectedInCatalog
    ? selectedID
    : visibleProducts[0]?.id || "";

  // Persist the fallback selection so a Disable from the default landing
  // leaves a URL anchor and the detail pane (with Enable) stays reachable.
  useEffect(() => {
    if (selectedID || visibleProducts.length === 0) return;
    setSearchParams({ product: visibleProducts[0].id }, { replace: true });
  }, [selectedID, setSearchParams, visibleProducts]);
  const productQuery = useProductQuery(selectedIDWithFallback);
  const allowed = can(identityQuery.data?.roles, "createRecords");
  const canManageProductStatus = can(
    identityQuery.data?.roles,
    "manageProductStatus",
  );
  const canManageFeatureCatalog = can(
    identityQuery.data?.roles,
    "manageFeatureCatalog",
  );
  const [statusTarget, setStatusTarget] = useState<Product>();
  const [featureCatalogTarget, setFeatureCatalogTarget] = useState<Product>();
  const statusMutation = useMutation({
    mutationFn: ({
      product,
      status,
      reason,
    }: {
      product: Product;
      status: "active" | "disabled";
      reason: string;
    }) =>
      adminConfig.demoMode
        ? Promise.resolve({ ...product, status })
        : adminApi.updateProductStatus(product.id, status, reason),
    onSuccess: async (updatedProduct) => {
      queryClient.setQueryData<{ products: Product[] }>(
        queryKeys.products,
        (current) => ({
          products: (current?.products ?? []).map((product) =>
            product.id === updatedProduct.id
              ? { ...product, status: updatedProduct.status }
              : product,
          ),
        }),
      );
      queryClient.setQueryData<Product>(
        queryKeys.product(updatedProduct.id),
        (current) =>
          current
            ? { ...current, status: updatedProduct.status }
            : updatedProduct,
      );
      setStatusTarget(undefined);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.products }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.product(updatedProduct.id),
        }),
      ]);
    },
  });
  const featureCatalogMutation = useMutation({
    mutationFn: ({
      product,
      featureCatalog,
      reason,
    }: {
      product: Product;
      featureCatalog: Record<string, EntitlementType>;
      reason: string;
    }) =>
      adminConfig.demoMode
        ? Promise.resolve({
            ...product,
            feature_catalog: featureCatalog,
          })
        : adminApi.updateProductFeatureCatalog(product.id, {
            feature_catalog: featureCatalog,
            reason,
          }),
    onSuccess: async (updatedProduct) => {
      queryClient.setQueryData<{ products: Product[] }>(
        queryKeys.products,
        (current) => ({
          products: (current?.products ?? []).map((product) =>
            product.id === updatedProduct.id
              ? {
                  ...product,
                  feature_catalog: updatedProduct.feature_catalog,
                }
              : product,
          ),
        }),
      );
      queryClient.setQueryData<Product>(
        queryKeys.product(updatedProduct.id),
        (current) =>
          current
            ? {
                ...current,
                feature_catalog: updatedProduct.feature_catalog,
              }
            : updatedProduct,
      );
      setFeatureCatalogTarget(undefined);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.products }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.product(updatedProduct.id),
        }),
      ]);
    },
    onError: async (error, variables) => {
      if (!(error instanceof ApiError) || error.status !== 409) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.products }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.product(variables.product.id),
        }),
      ]);
      const currentProduct = queryClient.getQueryData<Product>(
        queryKeys.product(variables.product.id),
      );
      if (currentProduct) setFeatureCatalogTarget(currentProduct);
    },
  });

  return (
    <PageFrame size="fluid">
      <PageHeader
        title={t("products.title", "Products")}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void productsQuery.refetch()}
              disabled={productsQuery.isFetching}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {productsQuery.isFetching
                ? t("common.refreshing", "Refreshing…")
                : t("common.refresh", "Refresh")}
            </Button>
            <Button
              variant="primary"
              disabled={!allowed}
              onClick={() => navigate("/admin/products/new")}
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("products.create", "Create product")}
            </Button>
          </>
        }
      />
      {!allowed && (
        <PermissionHint
          role={requiredRole("createRecords")}
          action="create products"
        />
      )}
      {productsQuery.isError ? (
        <InlineAlert
          title={t("products.loadError", "Products could not load")}
          message={apiErrorMessage(productsQuery.error)}
          onRetry={() => void productsQuery.refetch()}
        />
      ) : (
        <section
          className="flex min-h-0 flex-1 flex-col rounded-lg border border-line bg-panel overflow-hidden"
          aria-labelledby="products-heading"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-line px-5 py-3.5">
            <div className="flex items-center gap-3">
              <span className="flex size-8 items-center justify-center rounded-md border border-line bg-panel-raised text-muted">
                <Package className="size-4" aria-hidden="true" />
              </span>
              <div>
                <h2 id="products-heading" className="font-semibold text-ink">
                  {t("products.catalog", "Product catalog")}
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  {t("common.records", {
                    count: visibleProducts.length,
                    defaultValue: `${visibleProducts.length} records returned`,
                  })}
                </p>
              </div>
            </div>
            <Badge variant="info">
              {identityQuery.data?.roles.join(" / ") ??
                t("nav.accessPending", "Access pending")}
            </Badge>
          </div>
          {products.length > 0 && (
            <div className="shrink-0 border-b border-line flex items-center gap-2 p-3">
              <Input
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={t(
                  "products.filterPlaceholder",
                  "Filter by product name, ID, or key",
                )}
                aria-label="Filter products"
              />
              <Select
                className="w-40 shrink-0"
                aria-label={t("products.statusFilterAria", "Filter by status")}
                value={statusFilter}
                onValueChange={setStatusFilter}
                options={[
                  { value: "active", label: t("common.active", "Active") },
                  { value: "disabled", label: t("common.disabled", "Disabled") },
                  { value: "all", label: t("search.allStatuses", "All statuses") },
                ]}
              />
            </div>
          )}
          {productsQuery.isPending ? (
            <div className="p-4">
              <LoadingRows />
            </div>
          ) : products.length === 0 ? (
            <EmptyState
              title={t("products.noProducts", "No products returned")}
              message={t(
                "products.noProductsDesc",
                "Create a product or ask a License Manager to seed the catalog.",
              )}
              action={
                allowed ? (
                  <Button
                    variant="primary"
                    onClick={() => navigate("/admin/products/new")}
                  >
                    {t("products.create", "Create product")}
                  </Button>
                ) : undefined
              }
            />
          ) : visibleProducts.length === 0 && !selectedInCatalog ? (
            <EmptyState
              title={t("products.noMatch", "No matching products")}
              message={t(
                "products.noMatchDesc",
                "Try a different product name, ID, or key.",
              )}
            />
          ) : (
            <div className="flex-1 min-h-0 grid divide-y divide-line md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:divide-x md:divide-y-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto console-scrollbar divide-y divide-line">
                {visibleProducts.map((product) => (
                  <SelectableRow
                    className="flex items-center justify-between gap-4 px-5 py-4"
                    key={product.id}
                    onClick={() => setSearchParams({ product: product.id })}
                    selected={product.id === selectedIDWithFallback}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ink">
                        {product.name}
                      </span>
                      <span className="mt-1 block truncate font-mono text-xs text-muted">
                        {product.id} · {product.key}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge
                        variant={
                          product.status === "active" ? "success" : "neutral"
                        }
                      >
                        {product.status}
                      </Badge>
                      <ChevronRight
                        className="size-4 text-muted"
                        aria-hidden="true"
                      />
                    </span>
                  </SelectableRow>
                ))}
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto console-scrollbar">
                <ProductDetail
                  product={productQuery.data}
                  loading={productQuery.isPending}
                  error={productQuery.error}
                  canManageStatus={canManageProductStatus}
                  canManageFeatureCatalog={canManageFeatureCatalog}
                  onManageStatus={(product) => {
                    statusMutation.reset();
                    setStatusTarget(product);
                  }}
                  onManageFeatureCatalog={(product) => {
                    featureCatalogMutation.reset();
                    setFeatureCatalogTarget(product);
                  }}
                />
              </div>
            </div>
          )}
        </section>
      )}
      <ProductStatusDialog
        product={statusTarget}
        open={Boolean(statusTarget)}
        busy={statusMutation.isPending}
        error={statusMutation.error}
        onOpenChange={(open) => {
          if (!open) {
            statusMutation.reset();
            setStatusTarget(undefined);
          }
        }}
        onSubmit={(status, reason) => {
          if (statusTarget) {
            statusMutation.mutate({ product: statusTarget, status, reason });
          }
        }}
      />
      <ProductFeatureCatalogDialog
        product={featureCatalogTarget}
        open={Boolean(featureCatalogTarget)}
        busy={featureCatalogMutation.isPending}
        error={featureCatalogMutation.error}
        onOpenChange={(open) => {
          if (!open) {
            featureCatalogMutation.reset();
            setFeatureCatalogTarget(undefined);
          }
        }}
        onSubmit={(featureCatalog, reason) => {
          if (featureCatalogTarget) {
            featureCatalogMutation.mutate({
              product: featureCatalogTarget,
              featureCatalog,
              reason,
            });
          }
        }}
      />
    </PageFrame>
  );
}

function ProductDetail({
  product,
  loading,
  error,
  canManageStatus,
  canManageFeatureCatalog,
  onManageStatus,
  onManageFeatureCatalog,
}: {
  product: Product | undefined;
  loading: boolean;
  error: unknown;
  canManageStatus: boolean;
  canManageFeatureCatalog: boolean;
  onManageStatus: (product: Product) => void;
  onManageFeatureCatalog: (product: Product) => void;
}) {
  const { t } = useTranslation();
  if (loading)
    return (
      <div className="p-5">
        <LoadingRows count={2} />
      </div>
    );
  if (error)
    return (
      <div className="p-5">
        <InlineAlert
          title="Product detail could not load"
          message={apiErrorMessage(error)}
        />
      </div>
    );
  if (!product)
    return (
      <EmptyState
        title={t("products.details.selectProduct", "Select a product")}
        message={t(
          "products.details.selectProductDesc",
          "Choose a product to inspect its platforms and feature catalog.",
        )}
      />
    );
  const features = Object.entries(product.feature_catalog ?? {});
  return (
    <article className="p-5 sm:p-6" aria-labelledby="product-detail-heading">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.14em] text-muted">
            {t("products.details.eyebrow", "Product detail")}
          </p>
          <h3
            id="product-detail-heading"
            className="mt-2 text-xl font-semibold tracking-tight text-ink"
          >
            {product.name}
          </h3>
          <p className="mt-1 font-mono text-xs text-muted">{product.id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Badge variant={product.status === "active" ? "success" : "neutral"}>
            {product.status}
          </Badge>
          <Link
            className={buttonVariants({ variant: "outline", size: "sm" })}
            to={`/admin/products/${encodeURIComponent(product.id)}/usage?segment=paid&window=7d`}
          >
            <BarChart3 className="size-4" aria-hidden="true" />
            {t("products.details.viewAnalysis", "View analysis")}
          </Link>
          <Button
            variant="outline"
            size="sm"
            disabled={!canManageStatus}
            onClick={() => onManageStatus(product)}
          >
            {product.status === "active"
              ? t("products.status.disable", "Disable Product")
              : t("products.status.enable", "Enable Product")}
          </Button>
        </div>
      </div>
      <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-[0.12em] text-muted">
            {t("products.form.key", "Product key")}
          </dt>
          <dd className="mt-1 font-mono text-ink">{product.key}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-[0.12em] text-muted">
            {t("products.form.platforms", "Platforms")}
          </dt>
          <dd className="mt-1 text-ink">
            {product.platforms.join(", ") || "—"}
          </dd>
        </div>
      </dl>
      <section className="mt-6 border-t border-line pt-5">
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted">
            {t("products.form.features", "Feature catalog")}
          </h4>
          <Button
            variant="outline"
            disabled={!canManageFeatureCatalog}
            onClick={() => onManageFeatureCatalog(product)}
          >
            <Pencil className="size-3.5" aria-hidden="true" />
            {t("products.features.edit", "Add features")}
          </Button>
        </div>
        {!canManageFeatureCatalog ? (
          <div className="mt-2">
            <PermissionHint role={requiredRole("manageFeatureCatalog")} />
          </div>
        ) : null}
        {features.length === 0 ? (
          <p className="mt-2 text-xs text-muted">
            {t("products.details.noFeatures", "No feature definitions")}
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {features.map(([key, feature]) => (
              <div
                className="rounded-md border border-line bg-canvas p-3"
                key={key}
              >
                <span className="font-mono text-xs font-semibold text-ink">
                  {key}
                </span>
                <p className="mt-1 text-xs text-muted">{feature}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </article>
  );
}
